// G-C5: literales en euskera (default) y castellano. No existe aún (fuera de alcance
// de add-auth/add-backoffice) un endpoint que exponga la preferencia de idioma persistida
// del usuario, así que el default de instancia (euskera) se aplica siempre; `t(key, 'es')`
// queda disponible para cuando ese mecanismo exista.
export type Lang = 'eu' | 'es'

const DICT = {
  // 2026-10-23 (club: "hay traducciones que no están bien"): el tipo de bloque salía en crudo.
  bloque_pretemporada: { eu: 'Denboraldiaurrea', es: 'Pretemporada' },
  bloque_temporada: { eu: 'Denboraldia', es: 'Temporada' },
  bloque_unico: { eu: 'Bakarra', es: 'Único' },
  usuarios: { eu: 'Erabiltzaileak', es: 'Usuarios' },
  temporadas: { eu: 'Denboraldiak', es: 'Temporadas' },
  equipos: { eu: 'Taldeak', es: 'Equipos' },
  // Issue #54, 2026-10-02: "botones separando categorias" — volver del listado filtrado de equipos
  // de una categoría al menú de categorías.
  volver: { eu: 'Atzera', es: 'Volver' },
  historico: { eu: 'Historikoa', es: 'Histórico' },
  sinTemporadasCerradas: { eu: 'Oraindik ez dago denboraldi itxirik', es: 'Aún no hay temporadas cerradas' },
  crear: { eu: 'Sortu', es: 'Crear' },
  editar: { eu: 'Editatu', es: 'Editar' },
  editando: { eu: 'Editatzen', es: 'Editando' },
  eliminar: { eu: 'Ezabatu', es: 'Eliminar' },
  guardar: { eu: 'Gorde', es: 'Guardar' },
  cancelar: { eu: 'Utzi', es: 'Cancelar' },
  // Issue #57, 2026-10-05: "quitamos la opción de Deshabilitar y añadimos un botón de Eliminar que
  // permita borrar completamente al usuario" — mismo patrón de confirmación reforzada (escribir un
  // dato exacto) ya usado para el borrado forzado de un equipo.
  eliminarUsuarioConfirm: {
    eu: 'Ekintza hau ezin da desegin. Erabiltzailea erabat ezabatuko da (Auth0n barne) eta bere taldeetako esleipenak galduko dira.',
    es: 'Esta acción no se puede deshacer. El usuario se borrará por completo (también en Auth0) y perderá sus asignaciones de equipo.',
  },
  eliminarUsuarioEscribirEmail: {
    eu: 'Berrespen gisa, idatzi erabiltzailearen emaila ("{email}"):',
    es: 'Para confirmar, escribe el email del usuario ("{email}"):',
  },
  eliminarUsuarioNoCoincide: {
    eu: 'Emaila ez dator bat; ez da ezer ezabatu.',
    es: 'El email no coincide; no se ha borrado nada.',
  },
  resetPassword: { eu: 'Pasahitza berrezarri', es: 'Restablecer contraseña' },
  resetPasswordOk: { eu: 'Berrezarpen emaila bidali da', es: 'Email de restablecimiento enviado' },
  nombre: { eu: 'Izena', es: 'Nombre' },
  email: { eu: 'Emaila', es: 'Email' },
  rol: { eu: 'Rola', es: 'Rol' },
  estado: { eu: 'Egoera', es: 'Estado' },
  abierta: { eu: 'Irekita', es: 'Abierta' },
  cerrada: { eu: 'Itxita', es: 'Cerrada' },
  cerrarTemporada: { eu: 'Denboraldia itxi', es: 'Cerrar temporada' },
  cerrarTemporadaConfirm: {
    eu: 'Ekintza hau ezin da desegin. Temporadaren datu guztiak irakurtzeko soilik izango dira.',
    es: 'Esta acción no se puede deshacer. Todos los datos de la temporada quedarán en solo lectura.',
  },
  reabrirTemporada: { eu: 'Denboraldia berrireki', es: 'Reabrir temporada' },
  reabrirTemporadaConfirm: {
    eu: 'Denboraldia berriro irekiko da eta bere datuak berriro editatu ahal izango dira.',
    es: 'La temporada volverá a estar abierta y sus datos se podrán volver a editar.',
  },
  borrarTemporadaConfirm: {
    eu: 'Ekintza hau ezin da desegin. Denboraldia eta bere blokeak/jaiegunak ezabatuko dira. Talderik badu, ezabatu horiek aurretik.',
    es: 'Esta acción no se puede deshacer. Se borrará la temporada y sus bloques/festivos. Si tiene equipos, bórralos antes.',
  },
  borrarTemporadaConEquiposError: {
    eu: 'Denboraldiak taldeak ditu esleituta; ezabatu horiek aurretik.',
    es: 'La temporada tiene equipos asociados; bórralos primero.',
  },
  borrarEquipoConfirm: {
    eu: 'Ekintza hau ezin da desegin. Taldeak jokalari, saio edo jardunaldi baditu, ezin izango da ezabatu.',
    es: 'Esta acción no se puede deshacer. Si el equipo tiene jugadores, sesiones o jornadas, no podrá borrarse.',
  },
  borrarMiembroConfirm: {
    eu: 'Jokalaria taldetik erabat ezabatuko da (adibidez, inportazio bikoiztu batetik sortutako errenkada bat). Ez da bajan geratuko: desagertu egingo da.',
    es: 'El jugador se borrará por completo del equipo (por ejemplo, una fila duplicada de una importación repetida). No quedará como "de baja": desaparecerá.',
  },
  forzarBorrarEquipoConfirm: {
    eu: 'Nahi baduzu, taldea eta bere datu guztiak (saioak, jokalariak, jardunaldiak) behartuta ezaba daitezke. Ekintza hau ezin da desegin.',
    es: '¿Quieres forzar el borrado? Se eliminará el equipo junto con todos sus datos (sesiones, jugadores, jornadas). Esta acción no se puede deshacer.',
  },
  forzarBorrarEquipoEscribirNombre: {
    eu: 'Berrespen gisa, idatzi taldearen izena ("{nombre}"):',
    es: 'Para confirmar, escribe el nombre del equipo ("{nombre}"):',
  },
  forzarBorrarEquipoNoCoincide: {
    eu: 'Izena ez dator bat; ez da ezer ezabatu.',
    es: 'El nombre no coincide; no se ha borrado nada.',
  },
  confirmar: { eu: 'Berretsi', es: 'Confirmar' },
  importarJugadoreak: { eu: 'Jokalariak inportatu', es: 'Importar jugadores' },
  descargarPlantilla: { eu: 'Txantiloia jaitsi', es: 'Descargar plantilla' },
  formatoImport: {
    eu: 'Zutabeak: nombre (derrigorrezkoa), apellidos, alias, fecha_incorporacion (YYYY-MM-DD edo DD/MM/YYYY, aukerakoa: hutsik badago, gaurko data erabiltzen da), grupo (con_ficha/sin_ficha/entrenador, aukerakoa, con_ficha lehenetsia)',
    es: 'Columnas: nombre (obligatoria), apellidos, alias, fecha_incorporacion (YYYY-MM-DD o DD/MM/YYYY, opcional: si falta se usa la fecha de hoy), grupo (con_ficha/sin_ficha/entrenador, opcional, con_ficha por defecto)',
  },
  datosEquipo: { eu: 'Taldearen datuak', es: 'Datos del equipo' },
  minutosPorPeriodo: { eu: 'Zatiko minutuak', es: 'Minutos por periodo' },
  numPeriodos: { eu: 'Zati kopurua', es: 'Número de periodos' },
  diasEntrenamiento: { eu: 'Entrenamendu egunak', es: 'Días de entrenamiento' },
  anadirJugadorManual: { eu: '+ Jokalaria eskuz gehitu', es: '+ Añadir jugador' },
  apellidos: { eu: 'Abizenak', es: 'Apellidos' },
  vaciarPlantilla: { eu: 'Plantila hustu', es: 'Vaciar plantilla' },
  zonaPeligro: { eu: 'Arrisku-eremua', es: 'Zona de peligro' },
  fechaInicio: { eu: 'Hasiera data', es: 'Fecha de inicio' },
  fechaFin: { eu: 'Amaiera data', es: 'Fecha de fin' },
  vaciarPlantillaConfirm: {
    eu: 'Ekintza hau ezin da desegin. Taldeko jokalari guztiak ezabatuko dira (eta haien asistentzia/minutajea), taldea bera mantenduz.',
    es: 'Esta acción no se puede deshacer. Se borrarán todos los jugadores del equipo (y su asistencia/minutaje), manteniendo el equipo.',
  },
  vaciarPlantillaEscribirNumero: {
    eu: 'Berrespen gisa, idatzi ezabatuko diren jokalari kopurua ({n}):',
    es: 'Para confirmar, escribe el número de jugadores que se van a borrar ({n}):',
  },
  vaciarPlantillaNoCoincide: {
    eu: 'Zenbakia ez dator bat; ez da ezer ezabatu.',
    es: 'El número no coincide; no se ha borrado nada.',
  },
  conFichaOpcion: { eu: 'Fitxaduna', es: 'Con ficha' },
  sinFichaOpcion: { eu: 'Fitxarik gabekoa', es: 'Sin ficha' },
  entrenadorOpcion: { eu: 'Entrenatzailea', es: 'Entrenador' },
  delegadoOpcion: { eu: 'Ordezkaria', es: 'Delegado' },
  importResultado: {
    eu: 'Inportazioa amaitu da: {creados} sortuta, {duplicados} bikoiztuta (ez dira sortu, jada bazeuden).',
    es: 'Importación completada: {creados} creados, {duplicados} duplicados (no se han creado, ya existían).',
  },
  fechaIncorporacion: { eu: 'Sarrera data', es: 'Fecha de incorporación' },
  fechaBaja: { eu: 'Baja data', es: 'Fecha de baja' },
  grupo: { eu: 'Taldea', es: 'Grupo' },
  orden: { eu: 'Ordena', es: 'Orden' },
  sinPermiso: { eu: 'Baimenik ez', es: 'Sin permiso' },
  salir: { eu: 'Irten', es: 'Salir' },
  irAEquipoak: { eu: 'Nire taldeak', es: 'Mis equipos' },
  irAPanel: { eu: 'ALERTAra joan', es: 'Ir a ALERTA' },
  // Petición del club (2026-10-31): enlace al manual de usuario.
  ayuda: { eu: 'Laguntza', es: 'Ayuda' },
  // Petición del club (2026-10-31): estadísticas de uso, rendimiento y fallos (`admin/uso/UsoPage`).
  usoEstado: { eu: 'Erabilera eta egoera', es: 'Uso y estado' },
  usoIntro: {
    eu: 'Nork erabiltzen duen aplikazioa, zenbat denbora behar duen erantzuteko eta zer akats gertatu diren. Datuak 90 egunez gordetzen dira.',
    es: 'Quién usa la app, cuánto tarda en responder y qué fallos ha habido. Los datos se guardan 90 días.',
  },
  usoUltimosDias: { eu: 'Azken {n} egunak', es: 'Últimos {n} días' },
  usoActivosHoy: { eu: 'Gaur aktibo', es: 'Activos hoy' },
  usoActivosSemana: { eu: 'Aktibo 7 egunetan', es: 'Activos en 7 días' },
  usoActivosMes: { eu: 'Aktibo 30 egunetan', es: 'Activos en 30 días' },
  usoPeticiones: { eu: 'Eskaerak', es: 'Peticiones' },
  usoRespuestaTipica: { eu: 'Ohiko erantzuna', es: 'Respuesta típica' },
  usoRespuestaLenta: { eu: 'Erantzun motelenak (p95)', es: 'Respuestas lentas (p95)' },
  usoErrores: { eu: 'Akatsak', es: 'Fallos' },
  usoErroresDetalle: { eu: '{api} zerbitzarian · {web} nabigatzailean', es: '{api} en el servidor · {web} en el navegador' },
  usoPorDia: { eu: 'Erabiltzaile aktiboak egunez egun', es: 'Usuarios activos por día' },
  usoUsuarios: { eu: 'Erabiltzaileak', es: 'Usuarios' },
  usoUltimoAcceso: { eu: 'Azken sarbidea', es: 'Último acceso' },
  usoDiasActivo: { eu: 'Egun aktiboak', es: 'Días activo' },
  usoNunca: { eu: 'Inoiz ez', es: 'Nunca' },
  usoEquipos: { eu: 'Jarduera taldeka', es: 'Actividad por equipo' },
  usoEquiposAyuda: {
    eu: 'Lehendik dauden datuekin: asistentzia eta minutajea azkenekoz noiz aldatu ziren.',
    es: 'Con los datos que ya existían: cuándo se tocó por última vez la asistencia y el minutaje.',
  },
  usoUltimaAsistencia: { eu: 'Azken asistentzia', es: 'Última asistencia' },
  usoMarcas: { eu: 'Marka aldatuak', es: 'Marcas cambiadas' },
  usoUltimoMinutaje: { eu: 'Azken minutajea', es: 'Último minutaje' },
  usoFilasMinutaje: { eu: 'Minutaje-lerroak', es: 'Filas de minutaje' },
  usoRendimiento: { eu: 'Errendimendua (motelenak)', es: 'Rendimiento (las más lentas)' },
  usoRuta: { eu: 'Eskaera', es: 'Petición' },
  usoMaximo: { eu: 'Gehienez', es: 'Máximo' },
  usoListaErrores: { eu: 'Azken akatsak', es: 'Últimos fallos' },
  usoSinErrores: { eu: 'Ez da akatsik izan', es: 'No ha habido fallos' },
  usoSinDatos: { eu: 'Oraindik ez dago daturik', es: 'Todavía no hay datos' },
  usoFecha: { eu: 'Data', es: 'Fecha' },
  usoDonde: { eu: 'Non', es: 'Dónde' },
  usoMensaje: { eu: 'Mezua', es: 'Mensaje' },
  usoNavegador: { eu: 'Nabigatzailea', es: 'Navegador' },
  usoServidor: { eu: 'Zerbitzaria', es: 'Servidor' },
  // Menú lateral del backoffice (look & feel, 2026-10-23): títulos de las dos secciones.
  navAplicacion: { eu: 'Aplikazioa', es: 'Aplicación' },
  navBackoffice: { eu: 'Kudeaketa', es: 'Backoffice' },
  bloques: { eu: 'Blokeak', es: 'Bloques' },
  festivos: { eu: 'Jaiegunak', es: 'Festivos' },
  descripcion: { eu: 'Deskribapena', es: 'Descripción' },
  categoria: { eu: 'Kategoria', es: 'Categoría' },
  ukeEskola: { eu: 'UKE Eskola', es: 'UKE Eskola' },
  categoriaAsignada: { eu: 'Esleitutako kategoria', es: 'Categoría asignada' },
  equiposAsignados: { eu: 'Esleitutako taldeak', es: 'Equipos asignados' },
  entrenadoresAsignados: { eu: 'Esleitutako entrenatzaileak', es: 'Entrenadores asignados' },
  sinEntrenadores: { eu: 'Ez dago entrenatzailerik erregistratuta', es: 'No hay entrenadores registrados' },
  sinEquiposParaAsignar: {
    eu: 'Ez dago talderik esleitzeko (ez dago denboraldi irekirik edo ez du talderik).',
    es: 'No hay equipos disponibles para asignar (no hay ninguna temporada abierta con equipos).',
  },
  color: { eu: 'Kolorea', es: 'Color' },
  icono: { eu: 'Ikonoa', es: 'Icono' },
  fila: { eu: 'Errenkada', es: 'Fila' },
  campo: { eu: 'Eremua', es: 'Campo' },
  error: { eu: 'Errorea', es: 'Error' },
  lunes: { eu: 'A', es: 'L' },
  martes: { eu: 'A', es: 'M' },
  miercoles: { eu: 'A', es: 'X' },
  jueves: { eu: 'O', es: 'J' },
  viernes: { eu: 'O', es: 'V' },
  sabado: { eu: 'L', es: 'S' },
  domingo: { eu: 'I', es: 'D' },
} as const

export type DictKey = keyof typeof DICT


export function t(key: DictKey, lang: Lang): string {
  return DICT[key][lang]
}

// Issue #42, 2026-10-02: "Eskola" debe mostrarse como "UKE Eskola" y la F de F7/F11 en mayúscula
// en toda la app. `equipo.categoria` es un enum de Prisma en minúsculas (eskola/f7/f11); antes se
// renderizaba tal cual en varias pantallas. Cualquier valor no reconocido se devuelve sin tocar.
export function categoriaLabel(categoria: string, lang: Lang): string {
  if (categoria === 'eskola') return t('ukeEskola', lang)
  if (categoria === 'f7') return 'F7'
  if (categoria === 'f11') return 'F11'
  return categoria
}
